// apps/merchant-v3/src/lib/format/currency.ts
//
// Phase 6 Wave 2 Task 06 — Currency formatter + FX rate hook.
//
// Per F-01 M1 +: charging is ALWAYS USD. The FE renders the
// USD canonical amount and — when `merchant.displayCurrency !== 'USD'` —
// appends a parenthesised display-currency hint converted via the
// `/wallet/fx-rates` endpoint. The tooltip helper emits
// `{ usd, rate, fetchedAt, source }` so the consuming UI can spell out
// "Source of truth: USD. Rate: 0.92 EUR/USD @ 2026-05-08T00:00:00Z."
// .
//
// vg-binding: PLAN:task-06
// vg-binding: P6.D-131
// vg-binding: P6.D-133
// vg-binding: P6.D-221
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient, ApiError } from '@/lib/api/client';
import { useUser } from '@/store/user';

// --- types -----------------------------------------------------------------

/** `data` payload of GET /api/v1/wallet/fx-rates. */
export interface FxRatesData {
  base: 'USD';
  rates: {
    EUR: number;
    AUD: number;
    CAD: number;
    GBP: number;
    [code: string]: number; // forward-compat for future provider keys
  };
  fetchedAt: string; // ISO-8601
  source: string;
}

/** Identity rates — used as a 503/404 fallback so the UI keeps rendering
 *  USD even when the FX provider is degraded. */
const IDENTITY_RATES: FxRatesData = {
  base: 'USD',
  rates: { EUR: 1, AUD: 1, CAD: 1, GBP: 1 },
  fetchedAt: new Date(0).toISOString(),
  source: 'identity-fallback',
};

const FX_RATES_QUERY_KEY = ['wallet', 'fx-rates'] as const;
const ONE_HOUR_MS = 60 * 60 * 1000;

// --- pure formatters -------------------------------------------------------

/** Map user.language to a sensible BCP-47 locale tag for Intl.* APIs. */
export function localeFromUserLang(lang?: string): string {
  if (lang === 'VI') return 'vi-VN';
  if (lang === 'FR') return 'fr-FR';
  return 'en-US';
}

/**
 * Format an arbitrary numeric amount in a given currency using
 * `Intl.NumberFormat`. Examples:
 *   formatCurrency(1234.5, 'USD', 'en-US') → "$1,234.50"
 *   formatCurrency(1234.5, 'EUR', 'vi-VN') → "1.234,50 €"
 */
export function formatCurrency(
  amount: number,
  currency: string,
  locale: string = 'en-US',
): string {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
  }).format(amount);
}

/** Convert a USD amount into `to` using the provided FX rate map. */
export function convertCurrency(
  amountUsd: number,
  to: string,
  fxRates: FxRatesData['rates'],
): number {
  if (to === 'USD') return amountUsd;
  const rate = fxRates[to];
  if (!rate || !Number.isFinite(rate)) return amountUsd;
  return amountUsd * rate;
}

/**
 * Render a charged amount as `"$X.XX (≈ €Y.YY)"`. The first segment is
 * always USD canonical (per F-01 M1). When `displayCurrency` is USD or
 * the rate is missing, the secondary hint is omitted.
 */
export function formatMoney(
  amountUsd: number,
  displayCurrency: string,
  fx: FxRatesData | undefined,
  locale: string = 'en-US',
): string {
  const usdLabel = formatCurrency(amountUsd, 'USD', locale);
  if (!displayCurrency || displayCurrency === 'USD' || !fx) return usdLabel;
  const converted = convertCurrency(amountUsd, displayCurrency, fx.rates);
  if (converted === amountUsd) return usdLabel; // identity / missing rate
  const altLabel = formatCurrency(converted, displayCurrency, locale);
  return `${usdLabel} (≈ ${altLabel})`;
}

/**
 * Tooltip payload for the hover popover that explains the FX conversion
 * . Caller renders the wording localised via
 * `t('common:currency.tooltip', tooltipPayload)`.
 */
export interface CurrencyTooltipPayload {
  usd: string;
  display: string;
  rate: number;
  fetchedAt: string;
  source: string;
}

export function buildCurrencyTooltip(
  amountUsd: number,
  displayCurrency: string,
  fx: FxRatesData | undefined,
  locale: string = 'en-US',
): CurrencyTooltipPayload | null {
  if (!fx || displayCurrency === 'USD') return null;
  const rate = fx.rates[displayCurrency];
  if (!rate || !Number.isFinite(rate)) return null;
  return {
    usd: formatCurrency(amountUsd, 'USD', locale),
    display: displayCurrency,
    rate,
    fetchedAt: fx.fetchedAt,
    source: fx.source,
  };
}

// --- React Query hook ------------------------------------------------------

/**
 * Subscribe to `/wallet/fx-rates`. 1h staleTime / contract
 * spec (server cron refreshes daily; FE re-validates hourly). On 503
 * `FX_PROVIDER_UNAVAILABLE` or 404 (DEFERRED while P3 patch lands) we
 * return identity rates so call sites can keep rendering USD only.
 */
export function useFxRates(): UseQueryResult<FxRatesData, ApiError> {
  return useQuery<FxRatesData, ApiError>({
    queryKey: FX_RATES_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiClient.get<FxRatesData>('/api/v1/wallet/fx-rates');
      } catch (err) {
        //  fallback — provider unavailable OR endpoint not yet
        // shipped from P3 (404 DEFERRED). Return identity so UI keeps
        // rendering USD canonical without breaking.
        if (
          err instanceof ApiError &&
          (err.code === 'FX_PROVIDER_UNAVAILABLE' || err.status === 404)
        ) {
          return IDENTITY_RATES;
        }
        throw err;
      }
    },
    staleTime: ONE_HOUR_MS,
    gcTime: 24 * ONE_HOUR_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Returns the user's active display currency + a memo-stable formatter
 * bound to current FX rates. Pages call:
 *   const { format } = useDisplayCurrency();
 *   <span>{format(orderTotalUsd)}</span>
 */
export function useDisplayCurrency(): {
  displayCurrency: string;
  format: (amountUsd: number) => string;
  tooltip: (amountUsd: number) => CurrencyTooltipPayload | null;
  isLoading: boolean;
} {
  const user = useUser();
  const displayCurrency = user?.displayCurrency ?? 'USD';
  const locale = localeFromUserLang(user?.language);
  const fxQuery = useFxRates();

  return {
    displayCurrency,
    format: (amountUsd: number) =>
      formatMoney(amountUsd, displayCurrency, fxQuery.data, locale),
    tooltip: (amountUsd: number) =>
      buildCurrencyTooltip(amountUsd, displayCurrency, fxQuery.data, locale),
    isLoading: fxQuery.isLoading,
  };
}

export { FX_RATES_QUERY_KEY, IDENTITY_RATES };
